import type { RootState } from 'src/redux/store';

import { useMemo } from 'react';

import { useAppSelector } from 'src/hook/hooks';
import { isManagementRole } from 'src/permissions';

export function useManagementUser() {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
  const settingSub = useAppSelector((state: RootState) => (state as any).setting?.activeSubscription);
  const billingSub = useAppSelector((state: RootState) => (state as any).billing?.activeSubscription);

  const activeSubscription = settingSub ?? billingSub ?? null;

  const role =
    (loginApiData as any)?.role ||
    (loginApiData as any)?.roles?.[0]?.name ||
    (loginApiData as any)?.roles?.[0] ||
    (loginApiData as any)?.data?.role ||
    null;
  const isManagement = isManagementRole(role);

  const hasPaidPlan = useMemo(() => {
    if (!activeSubscription || typeof activeSubscription !== 'object') return false;

    // Check if subscription object or plan inside it exists
    const planObj = activeSubscription?.plan ?? activeSubscription;
    const status = activeSubscription?.status ?? planObj?.status;

    // If status is present, verify active state
    if (status && !['active', 'in_trial', 'non_renewing', 'paid', 'success'].includes(String(status).toLowerCase())) {
      return false;
    }

    // Has valid plan or subscription ID
    const hasPlanIdentifier = Boolean(
      planObj?.id ||
      planObj?.plan_id ||
      planObj?.name ||
      activeSubscription?.id ||
      activeSubscription?.subscriptionId
    );

    return Boolean(hasPlanIdentifier);
  }, [activeSubscription]);

  const isManagementWithoutPlan = Boolean(isManagement && !hasPaidPlan);

  const isBlockedModule = (moduleName: string): boolean => {
    const mod = (moduleName || '').toLowerCase().replace(/-/g, '_');
    return ['channel', 'campaign', 'workflow', 'ai_agents', 'ai_settings', 'ai_analytics'].includes(
      mod
    );
  };

  return {
    isManagement,
    hasPaidPlan,
    isManagementWithoutPlan,
    isBlockedModule,
    loginApiData,
    userId: (loginApiData as any)?.id || '',
  };
}
